import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FaBuilding, 
  FaBookOpen, 
  FaLock, 
  FaUnlock, 
  FaCheckCircle, 
  FaClock, 
  FaFileAlt, 
  FaQuestionCircle, 
  FaChevronDown, 
  FaChevronUp,
  FaMicrophone,
  FaLightbulb,
  FaArrowRight,
  FaShieldAlt,
  FaCalculator,
  FaTasks,
  FaGraduationCap,
  FaStar,
  FaFilter,
  FaAward,
  FaUserCheck,
  FaRegCheckCircle,
  FaHeadset,
  FaLaptop,
  FaKeyboard
} from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const AmazonHub = () => {
    const navigate = useNavigate();
    const [isPremium, setIsPremium] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const [activeCategory, setActiveCategory] = useState('all');
    
    // Interactive Score Diagnostic State for Amazon Candidates
    const [targetScore, setTargetScore] = useState(62);

    // 20 Mock Tests tailored for Amazon candidate screening
    const tests = Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        title: `Amazon Versant Practice Set ${i + 1}`,
        category: i % 3 === 0 ? 'vcs-voice' : i % 3 === 1 ? 'seller' : 'vcs-chat',
        targetRole: i % 3 === 0 ? 'Amazon VCS Voice Support' : i % 3 === 1 ? 'Amazon Seller Support Specialist' : 'Amazon VCS Non-Voice Chat',
        description: `40-question AI simulation matching Amazon Virtual Customer Service (VCS) screening standards across 9 core modules.`,
        duration: '35 mins',
        questions: 40,
        difficulty: i < 5 ? 'Easy' : i < 15 ? 'Medium' : 'Hard'
    }));

    useEffect(() => {
        const checkAccess = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const docRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && (docSnap.data().paidTests || docSnap.data().hasPaid)) {
                    setIsPremium(true);
                } else {
                    setIsPremium(false);
                }
            } else {
                setIsPremium(false);
            }
        };
        checkAccess();
    }, []);

    const handleStartTest = (testId) => {
        if (!isPremium && testId > 1) {
            navigate('/pricing');
            return;
        }
        navigate('/');
    };

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const filteredTests = tests.filter(test => {
        if (activeCategory === 'all') return true;
        return test.category === activeCategory;
    });

    const faqs = [
        {
            q: "How do I pass the Versant test for Amazon?",
            a: "To pass the Amazon Versant assessment, speak clearly at a steady conversational pace without long hesitations or artificial fillers, maintain 100% typing accuracy in dictation and sentence completion drills, and structure your email response logically with appropriate business greetings and clear problem resolution steps. Practicing with full-length timed AI mock tests beforehand significantly increases your pass rate."
        },
        {
            q: "Is it hard to pass the Amazon assessment test?",
            a: "The Amazon Versant test is not overly complex in difficulty, but its strict automated time limits and fast audio prompts catch unprepared candidates off guard. Because an automated AI engine evaluates your accent clarity, speech fluency, and written grammar, even minor long pauses or typing typos can reduce your score. Consistent practice under simulated exam timers makes passing straightforward."
        },
        {
            q: "What questions will be asked in the Versant test for Amazon?",
            a: "The Amazon Versant exam consists of 9 distinct audio and written modules. Spoken sections ask you to read aloud displayed text, repeat spoken customer sentences, construct sentences from jumbled audio phrases, answer short context questions, and retell short customer service scenarios. Written sections require sentence completion, sentence dictation, passage reconstruction, and a 100+ word workplace customer service email composition."
        },
        {
            q: "Is the Amazon Versant test spoken or written?",
            a: "The Amazon Versant test is a comprehensive 40-question assessment that evaluates BOTH spoken and written English skills. The first half tests spoken communication (pronunciation, fluency, sentence builds, and story retelling via microphone input), while the second half tests written proficiency (typing speed, spelling accuracy, sentence reconstruction, and email composition via keyboard input)."
        }
    ];

    return (
        <div className="bg-slate-50 min-h-screen text-slate-900 font-sans selection:bg-amber-200">
            <Helmet>
                <title>Amazon Versant Test 2026 | Free Practice & Score Guide</title>
                <meta name="description" content="What is the Amazon Versant test? Learn the format, required score, and practice free with AI-scored mock tests for Amazon customer service & support roles." />
                <link rel="canonical" href="https://versantpro.com/versant-test-for-amazon" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebPage",
                                "@id": "https://versantpro.com/versant-test-for-amazon/#webpage",
                                "url": "https://versantpro.com/versant-test-for-amazon",
                                "name": "Amazon Versant Test 2026 | Free Practice & Score Guide",
                                "description": "What is the Amazon Versant test? Learn the format, required score, and practice free with AI-scored mock tests for Amazon customer service & support roles."
                            },
                            {
                                "@type": "EducationalApplication",
                                "@id": "https://versantpro.com/versant-test-for-amazon/#application",
                                "name": "Amazon Versant Test AI Practice Simulator",
                                "operatingSystem": "All",
                                "applicationCategory": "EducationalApplication",
                                "offers": {
                                    "@type": "Offer",
                                    "price": "0.00",
                                    "priceCurrency": "USD"
                                }
                            },
                            {
                                "@type": "FAQPage",
                                "@id": "https://versantpro.com/versant-test-for-amazon/#faq",
                                "mainEntity": faqs.map(item => ({
                                    "@type": "Question",
                                    "name": item.q,
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": item.a
                                    }
                                }))
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <Header page="amazonHub" />

            {/* STICKY QUICK NAVIGATION BAR WITH ORIGINAL AMAZON AMBER HIGHLIGHTS */}
            <div className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur-md text-white border-b border-amber-500/20 py-2.5 px-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between text-xs font-semibold overflow-x-auto whitespace-nowrap gap-6 no-scrollbar">
                    <div className="flex items-center gap-2 text-amber-400 font-bold shrink-0">
                        <FaHeadset className="text-amber-400" /> Amazon Prep Portal
                    </div>
                    <div className="flex items-center gap-6">
                        <a href="#overview" className="hover:text-amber-400 transition-colors">Overview</a>
                        <a href="#amazon-matrix" className="hover:text-amber-400 transition-colors">Passing Scores</a>
                        <a href="#pipeline" className="hover:text-amber-400 transition-colors">Selection Process</a>
                        <a href="#modules" className="hover:text-amber-400 transition-colors">Exam Format</a>
                        <a href="#amazon-tests-grid" className="hover:text-amber-400 transition-colors font-bold text-amber-300">20 Practice Sets</a>
                        <a href="#guide" className="hover:text-amber-400 transition-colors">Prep Guide</a>
                        <a href="#faqs" className="hover:text-amber-400 transition-colors">FAQs</a>
                    </div>
                    <Link 
                        to="/"
                        className="bg-amber-400 hover:bg-amber-300 text-slate-950 px-3.5 py-1.5 rounded-lg font-black shrink-0 transition-transform active:scale-95 shadow-sm"
                    >
                        Free Test ➔
                    </Link>
                </div>
            </div>

            {/* ASYMMETRIC HERO SECTION WITH SMOOTH SOFT AMAZON ACCENTS (REDUCED HARSH CONTRAST) */}
            <section id="overview" className="relative w-full pt-12 pb-20 px-6 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white border-b border-amber-500/15">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-amber-500/15 rounded-full filter blur-3xl opacity-40 animate-blob"></div>

                <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-12 gap-10 items-center">
                    
                    {/* Left Column: Headline & Value Prop (7 cols) */}
                    <div className="lg:col-span-7 text-left space-y-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-amber-300 text-xs sm:text-sm font-bold border border-amber-400/30 shadow-md">
                            <span className="w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(251,191,36,0.8)]"></span>
                            Amazon VCS & Seller Support Hiring Guide (2026 Edition)
                        </div>

                        <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight drop-shadow-md">
                            Amazon Versant Test — <span className="text-amber-400">What It Is</span>, Score Needed & Free Practice
                        </h1>

                        <p className="text-base sm:text-lg text-slate-300 font-light leading-relaxed">
                            Complete preparation guide for Amazon Virtual Customer Service (VCS), Seller Support, and Customer Advisor screening. Practice with full-length AI mock tests matching Amazon's automated evaluation rules.
                        </p>

                        <div className="grid grid-cols-3 gap-3 pt-2 text-center text-xs">
                            <div className="bg-white/5 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-md">
                                <div className="text-amber-400 font-black text-lg">58–65+</div>
                                <div className="text-slate-300 text-[11px] font-medium">Amazon Cutoff Score</div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-md">
                                <div className="text-amber-300 font-black text-lg">Spoken & Written</div>
                                <div className="text-slate-300 text-[11px] font-medium">9 Timed Modules</div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-md">
                                <div className="text-amber-400 font-black text-lg">Instant AI</div>
                                <div className="text-slate-300 text-[11px] font-medium">GSE & CEFR Feedback</div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-4 pt-2">
                            <Link 
                                to="/"
                                className="px-8 py-4 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-sm rounded-2xl shadow-lg hover:scale-105 transition-all flex items-center gap-2"
                            >
                                Start Free Amazon Practice Test <FaArrowRight />
                            </Link>
                            <a 
                                href="#amazon-matrix"
                                className="px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-2xl border border-white/20 transition-colors"
                            >
                                View Passing Score Table 👇
                            </a>
                        </div>
                    </div>

                    {/* Right Column: Interactive Amazon Score Diagnostic Calculator Widget (5 cols) */}
                    <div className="lg:col-span-5 bg-white/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl space-y-6">
                        <div className="flex items-center justify-between border-b border-white/10 pb-4">
                            <div className="flex items-center gap-2 text-amber-300 font-bold text-sm">
                                <FaCalculator /> Amazon Score Diagnostic Calculator
                            </div>
                            <span className="text-[10px] uppercase bg-amber-400 text-slate-950 font-black px-2.5 py-0.5 rounded-full">
                                Interactive
                            </span>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-slate-200 flex justify-between">
                                <span>Target Amazon Assessment Score:</span>
                                <span className="text-amber-400 font-black text-sm">{targetScore} / 80</span>
                            </label>
                            <input 
                                type="range" 
                                min="48" 
                                max="75" 
                                value={targetScore} 
                                onChange={(e) => setTargetScore(Number(e.target.value))}
                                className="w-full accent-amber-400 cursor-pointer"
                            />
                        </div>

                        <div className="bg-slate-950/70 rounded-2xl p-4 border border-white/10 space-y-3 text-xs">
                            <div className="flex justify-between items-center text-slate-300">
                                <span>CEFR Alignment:</span>
                                <span className="font-extrabold text-amber-300 bg-amber-950/60 px-2.5 py-0.5 rounded border border-amber-500/30">
                                    {targetScore >= 65 ? 'C1 (Advanced Fluent)' : targetScore >= 58 ? 'B2 (Upper Intermediate)' : 'B1 (Intermediate)'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-slate-300">
                                <span>Amazon Role Qualification:</span>
                                <span className="font-extrabold text-amber-300 bg-amber-950/60 px-2.5 py-0.5 rounded border border-amber-500/30">
                                    {targetScore >= 62 ? 'Amazon VCS Voice Support' : targetScore >= 56 ? 'Seller Support Specialist' : 'VCS Chat & Non-Voice'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-slate-300">
                                <span>Recommended Practice:</span>
                                <span className="font-bold text-white">15 – 20 Full Mock Tests</span>
                            </div>
                        </div>

                        <Link 
                            to="/"
                            className="w-full py-3.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-extrabold text-xs rounded-xl flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-95"
                        >
                            Test Your Current Score Now ➔
                        </Link>
                    </div>

                </div>
            </section>

            {/* SELECTION PIPELINE WORKFLOW (4-Step Cards) */}
            <section id="pipeline" className="max-w-7xl mx-auto px-6 py-16 -mt-8 relative z-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="text-center max-w-2xl mx-auto space-y-2">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 text-amber-800 rounded-full text-xs font-bold uppercase tracking-widest border border-amber-200/80">
                            <FaTasks /> Candidate Journey
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Amazon Recruitment Pipeline: Where Versant Fits
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-500">Understanding Amazon's 4-stage virtual assessment workflow.</p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4 relative">
                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 relative group hover:border-amber-400 transition-all">
                            <div className="w-8 h-8 rounded-xl bg-slate-900 text-amber-400 font-black flex items-center justify-center text-xs mb-3 shadow-md">
                                01
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 mb-1">Online Application</h3>
                            <p className="text-[11px] text-slate-600 font-light leading-relaxed">
                                Submit resume for Virtual Customer Service (VCS), Seller Support, or Customer Advisor profiles via Amazon Jobs portal.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 relative group hover:border-amber-400 transition-all">
                            <div className="w-8 h-8 rounded-xl bg-slate-900 text-amber-400 font-black flex items-center justify-center text-xs mb-3 shadow-md">
                                02
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 mb-1">Cognitive & Work Style</h3>
                            <p className="text-[11px] text-slate-600 font-light leading-relaxed">
                                Automated test evaluating situational judgement, customer obsession principles, and basic logical reasoning.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-amber-50 via-white to-amber-50/40 rounded-2xl p-5 border-2 border-amber-400 relative group shadow-md scale-105 z-10">
                            <div className="absolute -top-2.5 right-3 px-2 py-0.5 bg-amber-400 text-slate-950 text-[9px] font-black uppercase rounded-full shadow-sm">
                                CRITICAL ELIMINATION
                            </div>
                            <div className="w-8 h-8 rounded-xl bg-amber-400 text-slate-950 font-black flex items-center justify-center text-xs mb-3 shadow-md">
                                03
                            </div>
                            <h3 className="text-sm font-extrabold text-slate-900 mb-1">Amazon Versant Assessment</h3>
                            <p className="text-[11px] text-slate-700 font-normal leading-relaxed">
                                Automated AI test measuring audio speech fluency, accent clarity, sentence dictation, passage reconstruction, and email typing.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 relative group hover:border-amber-400 transition-all">
                            <div className="w-8 h-8 rounded-xl bg-slate-900 text-amber-400 font-black flex items-center justify-center text-xs mb-3 shadow-md">
                                04
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 mb-1">Virtual HR & Operations</h3>
                            <p className="text-[11px] text-slate-600 font-light leading-relaxed">
                                Final virtual interview evaluating scenario handling, customer resolution empathy, and Amazon Leadership Principles.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* AMAZON SCORE MATRIX TABLE */}
            <section id="amazon-matrix" className="max-w-7xl mx-auto px-6 py-4">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-6">
                    <div className="border-b border-slate-100 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 text-amber-800 font-extrabold text-xs uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-full mb-2 border border-amber-200/80">
                                <FaShieldAlt /> Cutoff Thresholds
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                                Amazon Versant Score Requirements by Job Role
                            </h2>
                        </div>
                        <span className="text-xs text-slate-500 font-medium bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 self-start sm:self-auto">
                            Updated for 2026 Recruitment
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[650px]">
                            <thead>
                                <tr className="bg-slate-900 text-white text-xs font-black uppercase tracking-wider">
                                    <th className="p-4 rounded-l-xl">Amazon Profile</th>
                                    <th className="p-4">Cutoff Score</th>
                                    <th className="p-4">CEFR Level</th>
                                    <th className="p-4">Key Tested Competencies</th>
                                    <th className="p-4 rounded-r-xl">Priority Modules</th>
                                </tr>
                            </thead>
                            <tbody className="text-xs text-slate-600 divide-y divide-slate-100">
                                <tr className="hover:bg-amber-50/40 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 bg-amber-500 rounded-full"></span>
                                        Amazon VCS Voice Support
                                    </td>
                                    <td className="p-4 font-black text-amber-600 text-sm">62 – 68+ / 80</td>
                                    <td className="p-4 font-semibold">CEFR B2+ / C1</td>
                                    <td className="p-4">Pronunciation, Fluency & Active Listening</td>
                                    <td className="p-4 font-medium text-slate-800">Repeat Sentences & Story Retelling</td>
                                </tr>
                                <tr className="hover:bg-amber-50/40 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 bg-amber-500 rounded-full"></span>
                                        Amazon Seller Support Specialist
                                    </td>
                                    <td className="p-4 font-black text-amber-600 text-sm">58 – 64 / 80</td>
                                    <td className="p-4 font-semibold">CEFR B2</td>
                                    <td className="p-4">Technical Grammar & Business Email Syntax</td>
                                    <td className="p-4 font-medium text-slate-800">Passage Reconstruction & Email Writing</td>
                                </tr>
                                <tr className="hover:bg-amber-50/40 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 bg-amber-500 rounded-full"></span>
                                        Amazon VCS Non-Voice Chat
                                    </td>
                                    <td className="p-4 font-black text-amber-600 text-sm">55 – 60 / 80</td>
                                    <td className="p-4 font-semibold">CEFR B1+ / B2</td>
                                    <td className="p-4">Typing Speed, Dictation & Sentence Builds</td>
                                    <td className="p-4 font-medium text-slate-800">Sentence Completion & Dictation</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* EXAM FORMAT SECTION */}
            <section id="modules" className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="space-y-2">
                        <div className="inline-flex items-center gap-2 text-amber-800 font-extrabold text-xs uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-full border border-amber-200/80">
                            <FaBookOpen /> Comprehensive Format Breakdown
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            What is the Amazon Versant Test Format?
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-normal max-w-4xl">
                            If you are asking <em>"what is Versant test in Amazon"</em>, it is an automated AI-driven language assessment designed to evaluate how efficiently candidates communicate with global buyers and sellers. The exam contains 9 distinct sub-sections divided into spoken audio evaluation and written keyboard tasks:
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-slate-50 via-amber-50/20 to-white rounded-2xl p-6 border border-amber-200/80 space-y-5">
                            <div className="flex items-center justify-between">
                                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                                    <FaMicrophone className="text-amber-600" /> Audio Spoken Modules
                                </h3>
                                <span className="bg-amber-100 text-amber-900 text-[10px] font-black px-2.5 py-1 rounded-full uppercase border border-amber-200">
                                    Voice AI Evaluated
                                </span>
                            </div>
                            <ul className="text-xs sm:text-sm text-slate-600 space-y-3 list-none font-light">
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-amber-500 shrink-0 mt-0.5" />
                                    <div><strong>Read Aloud:</strong> Read displayed customer statements aloud with natural stress and smooth cadence.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-amber-500 shrink-0 mt-0.5" />
                                    <div><strong>Repeat Sentences:</strong> Listen to 16 spoken customer audio clips once and repeat them word-for-word.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-amber-500 shrink-0 mt-0.5" />
                                    <div><strong>Sentence Builds:</strong> Rearrange 3 jumbled audio fragments into a grammatically correct spoken sentence.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-amber-500 shrink-0 mt-0.5" />
                                    <div><strong>Story Retelling:</strong> Listen to a short 30s customer service scenario and summarize the details in your own words.</div>
                                </li>
                            </ul>
                            <div className="pt-2">
                                <Link to="/versant-speaking-and-listening-practice-test" className="text-xs font-extrabold text-amber-700 hover:text-amber-800 flex items-center gap-1">
                                    Practice Spoken Audio Modules <FaArrowRight className="text-[10px]" />
                                </Link>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200 space-y-5">
                            <div className="flex items-center justify-between">
                                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                                    <FaKeyboard className="text-slate-700" /> Keyboard Written Modules
                                </h3>
                                <span className="bg-slate-100 text-slate-800 text-[10px] font-black px-2.5 py-1 rounded-full uppercase border border-slate-200">
                                    Typing & Syntax AI
                                </span>
                            </div>
                            <ul className="text-xs sm:text-sm text-slate-600 space-y-3 list-none font-light">
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-slate-500 shrink-0 mt-0.5" />
                                    <div><strong>Sentence Completion:</strong> Fill in vocabulary context gaps in customer support transcripts.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-slate-500 shrink-0 mt-0.5" />
                                    <div><strong>Dictation & Spelling:</strong> Listen to customer audio prompts and type them out with 100% spelling precision.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-slate-500 shrink-0 mt-0.5" />
                                    <div><strong>Passage Reconstruction:</strong> Read a short 30s policy update and reconstruct key points in writing.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-slate-500 shrink-0 mt-0.5" />
                                    <div><strong>Customer Email Response:</strong> Write a 100+ word formal Amazon customer service email within 9 minutes.</div>
                                </li>
                            </ul>
                            <div className="pt-2">
                                <Link to="/versant-reading-and-writing-mock-test" className="text-xs font-extrabold text-slate-800 hover:text-slate-950 flex items-center gap-1">
                                    Practice Written Keyboard Modules <FaArrowRight className="text-[10px]" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 20 MOCK TESTS CUSTOM GRID WITH FEATURED CARD & CATEGORY FILTER */}
            <main id="amazon-tests-grid" className="max-w-7xl mx-auto px-6 py-12">
                
                {/* Header & Filter Controls */}
                <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-xl mb-8 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                        <div>
                            <span className="inline-flex items-center gap-2 text-amber-800 font-extrabold text-xs uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-full mb-1 border border-amber-200/80">
                                <FaAward /> Amazon Practice Exam Suite
                            </span>
                            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                                Amazon Versant Practice Sets (1–20)
                            </h2>
                            <p className="text-slate-500 text-xs sm:text-sm mt-1">Select a practice exam below to test under Amazon AI scoring standards.</p>
                        </div>

                        {/* Category Filter Pills */}
                        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                            <button 
                                onClick={() => setActiveCategory('all')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'all' 
                                        ? 'bg-slate-900 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                All (20 Sets)
                            </button>
                            <button 
                                onClick={() => setActiveCategory('vcs-voice')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'vcs-voice' 
                                        ? 'bg-amber-500 text-slate-950 shadow-md font-extrabold' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                VCS Voice
                            </button>
                            <button 
                                onClick={() => setActiveCategory('seller')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'seller' 
                                        ? 'bg-amber-600 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                Seller Support
                            </button>
                            <button 
                                onClick={() => setActiveCategory('vcs-chat')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'vcs-chat' 
                                        ? 'bg-slate-800 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                VCS Chat
                            </button>
                        </div>
                    </div>

                    {/* FEATURED TEST #1 BANNER CARD */}
                    <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white rounded-2xl p-6 sm:p-8 relative overflow-hidden border border-amber-400/30 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-3 z-10 max-w-2xl">
                            <div className="flex items-center gap-2 text-xs font-extrabold text-amber-400">
                                <FaStar /> FEATURED FREE DIAGNOSTIC MOCK TEST
                            </div>
                            <h3 className="text-xl sm:text-2xl font-black">Amazon Versant Practice Set 1 (100% Free)</h3>
                            <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed">
                                Full-length 40-question AI simulation matching Amazon Customer Service screening formats. Evaluates spoken fluency, dictation, grammar, and written email composition.
                            </p>
                            <div className="flex items-center gap-4 text-xs text-amber-200 pt-1">
                                <span className="flex items-center gap-1 font-semibold"><FaClock /> 35 Mins</span>
                                <span className="flex items-center gap-1 font-semibold"><FaFileAlt /> 40 Questions</span>
                                <span className="bg-amber-400/20 px-2.5 py-0.5 rounded-full border border-amber-400/40 text-amber-300 font-bold">Free Unlocked</span>
                            </div>
                        </div>
                        <button 
                            onClick={() => handleStartTest(1)}
                            className="z-10 px-8 py-4 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-sm rounded-2xl shadow-lg transition-all hover:scale-105 active:scale-95 shrink-0"
                        >
                            Start Amazon Test 1 Now ➔
                        </button>
                    </div>
                </div>

                {/* Grid of Remaining Tests */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTests.map((test) => {
                        const isLocked = !isPremium && test.id > 1;

                        return (
                            <div
                                key={test.id}
                                className={`relative group bg-white rounded-3xl p-6 border-2 transition-all duration-300 flex flex-col justify-between ${
                                    isLocked
                                      ? 'border-slate-200/80 bg-slate-50/50 opacity-90 hover:border-slate-300'
                                      : 'border-amber-100 hover:border-amber-400 shadow-md hover:shadow-2xl hover:-translate-y-1'
                                }`}
                            >
                                <div>
                                    <div className="flex items-center justify-between gap-2 mb-4">
                                        <span className="text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                            {test.targetRole}
                                        </span>
                                        {isLocked ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                                                <FaLock className="text-amber-600 text-[10px]" /> Pro Only
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                                                <FaUnlock className="text-emerald-600 text-[10px]" /> Unlocked
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="text-lg font-extrabold text-slate-900 group-hover:text-amber-600 transition-colors mb-2">
                                        {test.title}
                                    </h3>

                                    <p className="text-xs text-slate-600 font-light leading-relaxed mb-6">
                                        {test.description}
                                    </p>
                                </div>

                                <div className="space-y-4 pt-4 border-t border-slate-100">
                                    <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                                        <span className="flex items-center gap-1"><FaClock className="text-amber-600" /> {test.duration}</span>
                                        <span className="flex items-center gap-1"><FaFileAlt className="text-amber-600" /> {test.questions} Qs</span>
                                        <span className={`font-bold ${test.difficulty === 'Easy' ? 'text-emerald-600' : test.difficulty === 'Medium' ? 'text-amber-600' : 'text-rose-600'}`}>
                                            {test.difficulty}
                                        </span>
                                    </div>

                                    <button
                                        onClick={() => handleStartTest(test.id)}
                                        className={`w-full py-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${
                                            isLocked
                                                ? 'bg-slate-100 hover:bg-amber-400 hover:text-slate-950 text-slate-600 border border-slate-200 font-bold'
                                                : 'bg-amber-400 hover:bg-amber-300 text-slate-950 font-black shadow-md hover:shadow-lg'
                                        }`}
                                    >
                                        {isLocked ? (
                                            <>
                                                <FaLock className="text-[11px]" /> Unlock Pro Test Set
                                            </>
                                        ) : (
                                            <>
                                                Start Practice Exam ➔
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </main>

            {/* 800-1000 WORD RICH EDUCATIONAL GUIDE SECTION */}
            <section id="guide" className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-10">
                    
                    {/* Section 1: Intro */}
                    <div className="space-y-4">
                        <div className="inline-flex items-center gap-2 text-amber-800 font-extrabold text-xs uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-full border border-amber-200/80">
                            <FaBuilding /> Amazon Candidate Screening Guide
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Understanding the Amazon Versant Test Evaluation
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            Amazon relies on automated assessment tools like the <strong>Amazon Versant Test</strong> to screen thousands of global applicants applying for high-volume customer-facing roles. Positions such as <strong>Virtual Customer Service (VCS) Associate</strong>, <strong>Amazon Seller Support Specialist</strong>, and <strong>Customer Advisor</strong> require flawless English communication, active listening skills, and rapid typing execution.
                        </p>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            Unlike traditional interviews where human recruiters assess conversation flow, the Amazon Versant assessment is entirely automated via artificial intelligence. The speech evaluation engine analyzes your pronunciation, rhythm, cadence, and pause patterns, while the writing engine evaluates spelling precision, grammar syntax, passage recall, and professional email tone under tight timers.
                        </p>
                    </div>

                    {/* Section 2: Score Requirements */}
                    <div className="space-y-4 pt-6 border-t border-slate-100">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Amazon Versant Score Requirements & Cutoff Thresholds
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            The Versant test is scored on a scale from 20 to 80 points, mapped directly to global language standards such as the <strong>Global Scale of English (GSE)</strong> and the <strong>CEFR (Common European Framework of Reference for Languages)</strong>.
                        </p>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            For Amazon <strong>VCS Voice Support roles</strong>, candidates generally need a passing cutoff score of <strong>62 to 68+ points</strong> (CEFR B2+ / C1 level). For <strong>Amazon Seller Support</strong> and <strong>VCS Non-Voice Chat roles</strong>, cutoff scores typically hover between <strong>55 to 60 points</strong> (CEFR B1+ / B2 level). Achieving a high score demonstrates that you can resolve complex customer returns, shipping queries, and seller account escalations without miscommunication.
                        </p>
                    </div>

                    {/* Section 3: Preparation Tips */}
                    <div className="space-y-4 pt-6 border-t border-slate-100">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            4 Essential Preparation Tips for Amazon Candidates
                        </h2>

                        <div className="grid md:grid-cols-2 gap-5 pt-2">
                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs flex items-center justify-center font-bold">1</span>
                                    Quiet Room & Noise-Canceling Headset Setup
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    Background noise can cause the AI speech recognition engine to drop words or miscalculate fluency. Ensure you complete the test in a completely silent room using a USB wired headset with a boom microphone.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs flex items-center justify-center font-bold">2</span>
                                    Natural Cadence Without Filler Words
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    Avoid artificial pauses or filler sounds like "um," "ah," or "like." Speak at a steady, natural conversational speed even if you miss a single word in Repeat Sentences.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs flex items-center justify-center font-bold">3</span>
                                    Typing Speed & Spelling Accuracy Drills
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    For Dictation and Passage Reconstruction, practice typing accurately under time pressure. Spelled words with minor typos are marked incorrect by automated syntax filters.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs flex items-center justify-center font-bold">4</span>
                                    Master Business Email Structure
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    Format customer service email responses using clear sections: Professional Salutation, Apology & Context, Solution Steps, and Formal Sign-off to maximize organization points.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Banner CTA Box */}
                    <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white rounded-2xl p-6 sm:p-8 border border-amber-400/30 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl">
                        <div className="space-y-2 max-w-2xl">
                            <span className="inline-block px-3 py-1 bg-amber-400/20 text-amber-300 text-xs font-bold rounded-full border border-amber-400/40 uppercase tracking-wider">
                                🚀 Ready for your Amazon Virtual Assessment?
                            </span>
                            <h3 className="text-xl font-extrabold">Start Free Amazon Versant Practice Test</h3>
                            <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed">
                                Take a 100% free full-length AI mock test with instant score report and section-by-section feedback.
                            </p>
                        </div>
                        <Link 
                          to="/" 
                          className="px-8 py-4 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs rounded-xl shadow-lg transition-transform active:scale-95 whitespace-nowrap shrink-0"
                        >
                            Start Free Test Now ➔
                        </Link>
                    </div>

                </div>
            </section>

            {/* FREQUENTLY ASKED QUESTIONS SECTION */}
            <section id="faqs" className="max-w-7xl mx-auto px-6 py-8 pb-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="text-center max-w-2xl mx-auto space-y-2">
                        <div className="inline-flex items-center gap-2 text-amber-800 font-extrabold text-xs uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-full border border-amber-200/80">
                            <FaQuestionCircle /> Frequently Asked Questions
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Amazon Versant Assessment FAQs
                        </h2>
                    </div>

                    <div className="space-y-4 max-w-4xl mx-auto">
                        {faqs.map((faq, index) => (
                            <div 
                                key={index} 
                                className="border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200"
                            >
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full text-left p-5 bg-slate-50 hover:bg-slate-100 flex justify-between items-center gap-4 transition-colors font-bold text-slate-900 text-sm sm:text-base"
                                >
                                    <span>{faq.q}</span>
                                    {openFaq === index ? (
                                        <FaChevronUp className="text-amber-600 shrink-0 text-sm" />
                                    ) : (
                                        <FaChevronDown className="text-slate-400 shrink-0 text-sm" />
                                    )}
                                </button>
                                {openFaq === index && (
                                    <div className="p-5 bg-white text-xs sm:text-sm text-slate-600 font-light leading-relaxed border-t border-slate-100">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AmazonHub;
